import { useEffect, useState } from 'react';
import { endpoints, paymentRequired, type FamilyMember, type FamilyMemberInput } from '../api/endpoints';
import { ApiException } from '../api/client';
import { useAuth } from '../store/auth';
import { Skeleton, ErrorState, EmptyState } from '../components/states';
import { UpgradeModal } from '../components/UpgradeModal';

type Diet = FamilyMemberInput['diet'];
type Allergen = FamilyMemberInput['allergens'][number];

const DIETS: { value: Diet; label: string }[] = [
  { value: 'omnivore', label: 'Всеядный' },
  { value: 'vegetarian', label: 'Вегетарианец' },
  { value: 'vegan', label: 'Веган' },
  { value: 'halal', label: 'Халяль' },
  { value: 'kosher', label: 'Кошер' },
];
const ALLERGENS: { value: Allergen; label: string }[] = [
  { value: 'dairy', label: 'Молочное' },
  { value: 'gluten', label: 'Глютен' },
  { value: 'nuts', label: 'Орехи' },
  { value: 'seafood', label: 'Морепродукты' },
  { value: 'eggs', label: 'Яйца' },
  { value: 'soy', label: 'Соя' },
  { value: 'fish', label: 'Рыба' },
  { value: 'citrus', label: 'Цитрусовые' },
];
const EMPTY_FORM: FamilyMemberInput = { name: '', diet: 'omnivore', allergens: [] };

interface Editing {
  id?: string;
  form: FamilyMemberInput;
}

export function FamilyScreen() {
  // Спец-согласие (Часть 2) гейтит диету/аллергии: без него поля заблокированы, при сохранении
  // бэкенд форсит безопасные omnivore/[] (EMPTY_FORM их и так держит).
  const specialConsent = useAuth((s) => s.specialConsent);
  const setSpecialConsent = useAuth((s) => s.setSpecialConsent);
  const [members, setMembers] = useState<FamilyMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [allowing, setAllowing] = useState(false);
  const [upsell, setUpsell] = useState<{ requiredTier: 'pro' | 'family'; message: string } | null>(null);

  async function load() {
    setError(null);
    try {
      setMembers(await endpoints.getFamily());
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить семью');
    }
  }
  useEffect(() => {
    // Намеренный fetch-in-effect (план без TanStack): разовый setState на маунте — приемлемо.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, []);

  function startAdd() {
    setFormError(null);
    setEditing({ form: { ...EMPTY_FORM } });
  }
  function startEdit(m: FamilyMember) {
    setFormError(null);
    setEditing({ id: m.id, form: { name: m.name, diet: m.diet, allergens: [...(m.allergens ?? [])] } });
  }
  function toggleAllergen(a: Allergen) {
    setEditing((cur) => {
      if (!cur) return cur;
      const has = cur.form.allergens.includes(a);
      const allergens = has ? cur.form.allergens.filter((x) => x !== a) : [...cur.form.allergens, a];
      return { ...cur, form: { ...cur.form, allergens } };
    });
  }
  // Дать спец-согласие (Часть 2) прямо из формы — разблокирует диету/аллергии.
  async function allowSpecial() {
    setFormError(null);
    setAllowing(true);
    try {
      await endpoints.acceptConsent(true);
      setSpecialConsent(true);
    } catch (e) {
      setFormError(e instanceof ApiException ? e.body.message : 'Не удалось сохранить согласие');
    } finally {
      setAllowing(false);
    }
  }
  async function save() {
    if (!editing) return;
    if (editing.form.name.trim() === '') {
      setFormError('Введите имя');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing.id) await endpoints.updateFamilyMember(editing.id, editing.form);
      else await endpoints.addFamilyMember(editing.form);
      setEditing(null);
      await load();
    } catch (e) {
      const pr = paymentRequired(e);
      if (pr) {
        setEditing(null);
        setUpsell(pr);
        return;
      }
      setFormError(e instanceof ApiException ? e.body.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  }
  async function del(id?: string) {
    if (!id) return;
    try {
      await endpoints.deleteFamilyMember(id);
      await load();
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось удалить');
    }
  }

  if (members === null && !error) {
    return (
      <div className="space-y-3 px-4 pt-6">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-4 pt-6">
        <ErrorState message={error} onRetry={() => void load()} />
      </div>
    );
  }

  return (
    <div className="px-4 pt-6">
      <h1 className="mb-1 text-2xl font-bold text-ink">Семья</h1>
      <p className="mb-4 text-sm text-ink/60">Диеты и аллергии учитываются в рекомендациях.</p>

      {members!.length === 0 && !editing && (
        <EmptyState
          title="Пока никого нет"
          hint="Добавьте членов семьи, чтобы рекомендации учитывали их диеты и аллергии."
        />
      )}

      <ul className="space-y-3">
        {members!.map((m) => (
          <li key={m.id} className="rounded-2xl bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-ink">{m.name}</p>
                <p className="text-sm text-ink/60">
                  {DIETS.find((d) => d.value === m.diet)?.label}
                  {m.allergens && m.allergens.length > 0 &&
                    ` · без: ${m.allergens
                      .map((a) => ALLERGENS.find((x) => x.value === a)?.label ?? a)
                      .join(', ')}`}
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => startEdit(m)} className="text-sm text-primary-600">
                  Изм.
                </button>
                <button onClick={() => void del(m.id)} className="text-sm text-ink/40">
                  Удалить
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {editing ? (
        <div className="mt-4 rounded-2xl bg-white p-4 shadow-sm">
          <input
            aria-label="Имя"
            value={editing.form.name}
            onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })}
            placeholder="Имя"
            className="mb-3 w-full rounded-xl border border-black/10 px-4 py-3"
          />
          {!specialConsent && (
            <div className="mb-3 rounded-xl bg-amber-50 p-3 text-sm text-ink/70">
              <p className="mb-2">
                Чтобы учитывать диету и аллергии, разрешите обработку данных о здоровье.
              </p>
              <button
                type="button"
                onClick={() => void allowSpecial()}
                disabled={allowing}
                className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white disabled:bg-gray-300"
              >
                {allowing ? 'Сохраняем…' : 'Разрешить'}
              </button>
            </div>
          )}
          <label className="mb-1 block text-sm text-ink/60" htmlFor="diet-select">
            Диета
          </label>
          <select
            id="diet-select"
            value={editing.form.diet}
            disabled={!specialConsent}
            onChange={(e) =>
              setEditing({ ...editing, form: { ...editing.form, diet: e.target.value as Diet } })
            }
            className="mb-3 w-full rounded-xl border border-black/10 px-4 py-3 disabled:cursor-not-allowed disabled:bg-black/5 disabled:text-ink/40"
          >
            {DIETS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
          <p className="mb-1 text-sm text-ink/60">Аллергии</p>
          <div className="mb-3 flex flex-wrap gap-2">
            {ALLERGENS.map((a) => {
              const on = editing.form.allergens.includes(a.value);
              return (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => toggleAllergen(a.value)}
                  aria-pressed={on}
                  disabled={!specialConsent}
                  className={`rounded-full px-3 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                    on ? 'bg-primary-600 text-white' : 'bg-black/5 text-ink/70'
                  }`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
          {formError && (
            <p className="mb-3 text-sm text-red-600" role="alert">
              {formError}
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => void save()}
              disabled={saving}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-3 font-semibold text-white disabled:bg-gray-300"
            >
              Сохранить
            </button>
            <button onClick={() => setEditing(null)} className="rounded-xl px-4 py-3 text-ink/60">
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={startAdd}
          className="mt-4 w-full rounded-xl border border-primary-600 px-4 py-3 font-semibold text-primary-600"
        >
          + Добавить
        </button>
      )}

      {upsell && <UpgradeModal requiredTier={upsell.requiredTier} message={upsell.message} onClose={() => setUpsell(null)} />}
    </div>
  );
}
